import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { fetchApi } from './fetchApi';


// ✅ ggf. Pfad anpassen (so wie bei dir)
import EtikettenDruckDialog from '../components/EtikettenDruckDialog';

// ✅ Thumbnail-Komponente
import ArtikelThumbnail from './ArtikelThumbnail';

interface Child {
  id: number;
  sku: string;
  bezeichnung: string | null;
  farbe: string | null;
  groesse: string | null;
  bestand: number; // wird dynamisch geladen
  bild_url?: string | null; // ✅ NEU
}

interface Parent {
  id: number;
  sku: string;
  bezeichnung: string | null;
  kategorie_id?: number | null;
  children: Child[];
}

interface Kategorie {
  id: number;
  name: string;
}

const ALLE_KATEGORIEN = '__ALLE_KATEGORIEN__';
const ALLE_PARENTS = '__ALLE_PARENTS__';

export default function ParentChildListe() {
  const [daten, setDaten] = useState<Parent[]>([]);
  const [kategorien, setKategorien] = useState<Kategorie[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [erfolg, setErfolg] = useState<string | null>(null);


  const [selectedKategorieId, setSelectedKategorieId] = useState<string>(ALLE_KATEGORIEN);
  const [selectedParentId, setSelectedParentId] = useState<string>(ALLE_PARENTS);

  const [exportLaeuft, setExportLaeuft] = useState(false);

  // ✅ Etiketten-Dialog State
  const [etikettenOpen, setEtikettenOpen] = useState(false);
  const [etikettenInitialIds, setEtikettenInitialIds] = useState<number[]>([]);

  const navigate = useNavigate();

  // ✅ Robust gegen Props-Änderungen in ArtikelThumbnail.tsx (src/url/onClick)
  const ArtikelThumbnailAny = ArtikelThumbnail as any;

  // ----------------------------------------------------
  // Parent/Child-Daten laden
  // ----------------------------------------------------
 async function ladeDaten() {
  try {
    setFehler(null);
    setErfolg(null);

    const res = await fetchApi('/api/artikel/parent-child');
    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.error || 'Fehler beim Laden der Daten.');
    }

    const mitBestand = await Promise.all(
      (data as Parent[]).map(async (parent: Parent) => {
        const childrenMitBestand = await Promise.all(
          (parent.children || []).map(async (child: Child) => {
            const bestandRes = await fetchApi(`/api/bestand/menge/${child.id}`);
            const bestandData = await bestandRes.json();

            return {
              ...child,
              bestand: bestandData?.total || 0,
            };
          })
        );

        return {
          ...parent,
          children: childrenMitBestand,
        };
      })
    );

    setDaten(mitBestand);
  } catch (err: any) {
    console.error('❌ Fehler beim Laden der Parent/Child-Daten:', err);
    setFehler(
      err?.message === '401'
        ? 'Session abgelaufen. Bitte neu einloggen.'
        : 'Fehler beim Laden der Daten.'
    );
  }
}


  useEffect(() => {
    ladeDaten();
  }, []);

  // ----------------------------------------------------
  // Kategorien laden
  // ----------------------------------------------------
  useEffect(() => {
    async function ladeKategorien() {
      try {
        const resKat = await fetchApi('/api/kategorie');
        const dataKat = await resKat.json();
        if (!resKat.ok) throw new Error(dataKat.error || 'Fehler beim Laden der Kategorien.');
        setKategorien(dataKat as Kategorie[]);
      } catch (err) {
        console.error('Fehler beim Laden der Kategorien:', err);
      }
    }
    ladeKategorien();
  }, []);

  // ----------------------------------------------------
  // Helfer: Kategorie-Name für Parent
  // ----------------------------------------------------
  function getKategorieNameForParent(parent: Parent): string | null {
    if (parent.kategorie_id == null) return null;
    const kat = kategorien.find((k) => k.id === parent.kategorie_id);
    return kat ? kat.name : null;
  }

  // ----------------------------------------------------
  // Parent-Optionen für Parent-Dropdown (abhängig von Kategorie)
  // ----------------------------------------------------
  const parentOptionen: Parent[] = useMemo(() => {
    return daten.filter((p) => {
      if (selectedKategorieId === ALLE_KATEGORIEN) return true;
      const selIdNum = Number(selectedKategorieId);
      if (Number.isNaN(selIdNum)) return true;
      return p.kategorie_id === selIdNum;
    });
  }, [daten, selectedKategorieId]);

  // ----------------------------------------------------
  // Gefilterte Parents für die Anzeige
  // ----------------------------------------------------
  const gefilterteParents: Parent[] = useMemo(() => {
    return daten.filter((p) => {
      if (selectedKategorieId !== ALLE_KATEGORIEN) {
        const selIdNum = Number(selectedKategorieId);
        if (!Number.isNaN(selIdNum) && p.kategorie_id !== selIdNum) return false;
      }

      if (selectedParentId !== ALLE_PARENTS) {
        const pid = Number(selectedParentId);
        if (!Number.isNaN(pid) && p.id !== pid) return false;
      }

      return true;
    });
  }, [daten, selectedKategorieId, selectedParentId]);

  // ----------------------------------------------------
  // Filter-Handler
  // ----------------------------------------------------
  function handleKategorieChange(e: SelectChangeEvent<string>) {
    const value = e.target.value;
    setSelectedKategorieId(value);
    setSelectedParentId(ALLE_PARENTS);
  }

  function handleParentChange(e: SelectChangeEvent<string>) {
    const value = e.target.value;
    setSelectedParentId(value);
  }

  // ----------------------------------------------------
  // Inventur-Export (CSV)
  // ----------------------------------------------------
  async function handleInventurExport() {
    setExportLaeuft(true);
    setFehler(null);

    try {
      const res = await fetchApi('/api/inventur/export');
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Fehler beim Inventur-Export.');
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'inventur.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('❌ Fehler beim Inventur-Export:', err);
      setFehler(err.message || 'Fehler beim Inventur-Export.');
    } finally {
      setExportLaeuft(false);
    }
  }

  // ----------------------------------------------------
  // Löschen Child
  // ----------------------------------------------------
  async function childLoeschen(id: number, bestand: number) {
    setFehler(null);
    setErfolg(null);

    if (bestand > 0) {
      setFehler(`Löschen nicht möglich: Artikel hat noch ${bestand} Stück Bestand.`);
      return;
    }

    if (!window.confirm('Child-Artikel wirklich löschen?')) return;

    try {
      const res = await fetchApi(`/api/artikel/${id}`, { method: 'DELETE' });
      const data = await res.json();

      if (!res.ok) {
        setFehler(data.error || 'Löschen nicht möglich.');
        return;
      }

      setErfolg('Child-Artikel erfolgreich gelöscht.');
      ladeDaten();
    } catch {
      setFehler('Fehler beim Löschen des Artikels.');
    }
  }

  // ----------------------------------------------------
  // Löschen Parent
  // ----------------------------------------------------
  async function parentLoeschen(id: number, childAnzahl: number) {
    setFehler(null);
    setErfolg(null);

    if (childAnzahl > 0) {
      setFehler(`Löschen nicht möglich: Parent hat noch ${childAnzahl} Child-Artikel.`);
      return;
    }

    if (!window.confirm('Parent-Artikel wirklich löschen?')) return;

    try {
      const res = await fetchApi(`/api/artikel/${id}`, { method: 'DELETE' });
      const data = await res.json();

      if (!res.ok) {
        setFehler(data.error || 'Löschen nicht möglich.');
        return;
      }

      setErfolg('Parent-Artikel erfolgreich gelöscht.');
      ladeDaten();
    } catch {
      setFehler('Fehler beim Löschen des Artikels.');
    }
  }

  // ----------------------------------------------------
  // Artikeloptionen für Etiketten-Dialog (nur CHILDs)
  // ----------------------------------------------------
  const artikelOptionen = useMemo(() => {
    const map = new Map<number, { id: number; sku: string; bezeichnung: string }>();

    for (let p = 0; p < daten.length; p++) {
      const parent = daten[p];
      for (let c = 0; c < (parent.children || []).length; c++) {
        const child = parent.children[c];
        map.set(child.id, {
          id: child.id,
          sku: child.sku,
          bezeichnung: child.bezeichnung || '',
        });
      }
    }

    return Array.from(map.values());
  }, [daten]);

  return (
    <Container maxWidth="md">
      <Box mt={4} mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h5">Parent / Child Übersicht</Typography>

        <Button variant="outlined" onClick={handleInventurExport} disabled={exportLaeuft}>
          Inventur (CSV)
        </Button>
      </Box>

      {fehler && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {fehler}
        </Alert>
      )}
      {erfolg && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {erfolg}
        </Alert>
      )}

      {/* FILTER */}
      <Box mb={3} display="flex" gap={2} flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Kategorie</InputLabel>
          <Select label="Kategorie" value={selectedKategorieId} onChange={handleKategorieChange}>
            <MenuItem value={ALLE_KATEGORIEN}>Alle Kategorien</MenuItem>
            {kategorien.map((k) => (
              <MenuItem key={k.id} value={String(k.id)}>
                {k.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel>Parent-Artikel</InputLabel>
          <Select label="Parent-Artikel" value={selectedParentId} onChange={handleParentChange}>
            <MenuItem value={ALLE_PARENTS}>Alle Parent-Artikel</MenuItem>
            {parentOptionen.map((p) => (
              <MenuItem key={p.id} value={String(p.id)}>
                {p.sku} – {p.bezeichnung || ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* LISTE */}
      <List>
        {gefilterteParents.map((parent) => {
          const katName = getKategorieNameForParent(parent);

          return (
            <React.Fragment key={parent.id}>
              <ListItem>
                <ListItemText
                  primary={`${parent.sku} – ${parent.bezeichnung || ''}`}
                  secondary={katName ? `Kategorie: ${katName}` : undefined}
                  primaryTypographyProps={{ fontWeight: 'bold' }}
                />

                <Button
                  variant="outlined"
                  color="primary"
                  sx={{ mr: 1 }}
                  onClick={() => navigate(`/artikel/parent/bearbeiten/${parent.id}`)}
                >
                  bearbeiten
                </Button>

                <Button
                  variant="outlined"
                  color="error"
                  disabled={(parent.children || []).length > 0}
                  onClick={() => parentLoeschen(parent.id, (parent.children || []).length)}
                >
                  Löschen
                </Button>
              </ListItem>

              {/* CHILDREN */}
              {(parent.children || []).length > 0 && (
                <List component="div" disablePadding>
                  {parent.children.map((child) => {
                    const titel = `${child.sku}${child.bezeichnung ? ` – ${child.bezeichnung}` : ''}`;

                    return (
                      <ListItem key={child.id} sx={{ pl: 4, alignItems: 'center', gap: 1 }}>
                        {/* ✅ Thumbnail neben SKU (No-Image Default handled in ArtikelThumbnail) */}
                        <Box sx={{ mr: 1.5, flex: '0 0 auto' }}>
                          <ArtikelThumbnailAny
                            // kompatibel mit beiden Varianten:
                            src={child.bild_url || null}
                            url={child.bild_url || null}
                            alt={titel}
                            size={40}
                          />
                        </Box>

                        <ListItemText
                          primary={`${child.sku}`}
                          secondary={[
                            child.bezeichnung || '',
                            child.farbe || '',
                            child.groesse || '',
                            child.bestand > 0 ? `Bestand: ${child.bestand}` : '',
                          ]
                            .filter(Boolean)
                            .join(' • ')}
                        />

                        <Button
                          variant="outlined"
                          color="primary"
                          sx={{ mr: 1 }}
                          onClick={() => navigate(`/artikel/child/bearbeiten/${child.id}`)}
                        >
                          bearbeiten
                        </Button>

                        <Button
                          variant="outlined"
                          sx={{ mr: 1 }}
                          onClick={() => {
                            setEtikettenInitialIds([child.id]);
                            setEtikettenOpen(true);
                          }}
                        >
                          Etiketten
                        </Button>

                        <Button
                          variant="outlined"
                          color="error"
                          disabled={child.bestand > 0}
                          onClick={() => childLoeschen(child.id, child.bestand)}
                        >
                          Löschen
                        </Button>
                      </ListItem>
                    );
                  })}
                </List>
              )}

              <Divider sx={{ my: 2 }} />
            </React.Fragment>
          );
        })}
      </List>

      {/* Etiketten-Dialog */}
      <EtikettenDruckDialog
        open={etikettenOpen}
        onClose={() => setEtikettenOpen(false)}
        artikelOptionen={artikelOptionen}
        initialArtikelIds={etikettenInitialIds}
      />
    </Container>
  );
}
